import { SHAPE_CLASS, type Shape } from "./shape.js";
import { STATE_RING } from "./state-ring.js";
import { cn } from "./utils.js";

/* The one 32px chrome-control recipe (workspace-control-button, design-system
   programme wave 5). Engine tier and not in the barrel, so every tier can
   read it and it never moves: WorkspaceControlButton (shell) reads rail and
   primary, ChromeIconButton reads bar and quiet, and a Button inside a rail
   group reads rail and primary. It imports engine files only.

   It owns the 32px box, the disabled state (rail and primary), the pressed teal selection, the
   current-page paint and the rest paint. Each look is a complete class list:
   box, default corner, rest paint, hover and focus. The three rest looks keep
   today's classes:
   - rail: today's WORKSPACE_CONTROL_BUTTON_CLASS, except the hover. It is
     the neutral hover:pb-overlay-paint (ADR-0092 hover-quiet) and not
     hover:bg-pb-teal-soft, because teal marks the selected state only
     (design-owner decision 3).
   - bar: the top bar's utility buttons (TopBar, NotificationsPopover).
   - quiet: today's TabStrip New-tab and CardChrome icon buttons, one grey
     step up from muted to secondary text (SN-02, PR 3).
   The fourth, primary, is Button primary's paint on the 32px box: today's
   SolvePhaseStrip Advance (Button primary size sm with h-8 gap-1.5
   rounded-full text-sm font-medium). lib/ cannot import atoms, so it repeats
   the primary paint of buttonVariants, and a unit test stops drift.

   Every look takes the one focus-ring (ADR-0092; AP-02 moved bar and quiet
   off their ring-2 teal ring in PR 3). The looks are not merged otherwise:
   the default is today's look (plan section 5). 32px is never a public
   Button size.

   The fifth, toolbar (floating-toolbar, wave 6), is the canvas toolbar's
   28px control: today's ToolbarButton, and with iconOnly today's
   ToolbarIconButton. It keeps today's `rounded` corner, focus-ring and its
   own disabled state (muted text, no fade), so both lists are today's
   classes. `current` paints its active state.

   Literal class strings so Tailwind sees them. */

/** The disabled state every look takes. */
const CHROME_CONTROL_DISABLED =
  "disabled:pointer-events-none disabled:opacity-50";

/** The teal selection of a pressed control (C7: teal marks selection only),
 *  ringed in high contrast where the tint alone is too faint (d15). */
const CHROME_CONTROL_PRESSED = `bg-pb-teal-soft text-pb-teal-text ${STATE_RING.teal}`;

/** The current-page paint: today's TopBar Home while its route is current.
 *  The grey fill barely shows in high contrast, so the state ring does (d16). */
const CHROME_CONTROL_CURRENT = `bg-pb-card-elevated text-pb-text ${STATE_RING.teal}`;

/* A look whose current paint is not the default. Today's TopBar Home adds
   bg-pb-card-elevated text-pb-text to a class string that already holds
   text-pb-text-secondary, and text-pb-text-secondary wins by stylesheet order,
   so its glyph stays the secondary colour. The bar look keeps that painted
   colour: it adds only the fill (cn would otherwise drop the secondary text). */
const OWN_CURRENT: Partial<Record<ChromeControlLook, string>> = {
  bar: `bg-pb-card-elevated ${STATE_RING.teal}`,
};

/** Button primary's paint: the primary entry of buttonVariants, unchanged. */
export const CHROME_CONTROL_PRIMARY_PAINT =
  "bg-linear-to-b from-pb-accent-hover to-pb-accent text-pb-accent-foreground shadow-glow-accent-rest hover:from-pb-accent-hover hover:to-pb-accent-hover hover:shadow-glow-accent active:from-pb-accent-active active:to-pb-accent-active active:shadow-glow-accent-rest";

export const CHROME_CONTROL_LOOK = {
  /** The workspace rail: a 32px pill with a label, the neutral hover. */
  rail: `flex h-8 items-center gap-1 rounded-full px-2 text-sm font-medium text-pb-text transition-[gap,padding,color,background-color] duration-motion-state ease-motion-standard hover:pb-overlay-paint hover:text-pb-text focus-ring ${CHROME_CONTROL_DISABLED}`,
  /** The top bar's utility buttons (Search, the bell): a 32px square. */
  bar: "inline-flex h-8 w-8 items-center justify-center rounded-md text-pb-text-secondary hover:text-pb-text hover:pb-overlay-paint transition-colors focus-ring",
  /** Muted chrome icons (TabStrip New tab, CardChrome): a 32px square. */
  quiet:
    "flex h-8 w-8 items-center justify-center rounded-md text-pb-text-secondary transition-colors hover:pb-overlay-paint hover:text-pb-text focus-ring",
  /** The region's one commit (invariant 9) on the 32px pill box. */
  primary: `inline-flex h-8 items-center justify-center gap-1.5 rounded-full px-2 text-sm font-medium transition-[color,background-color,background-image,border-color,box-shadow,transform] motion-reduce:transition-none focus-ring press ${CHROME_CONTROL_DISABLED} ${CHROME_CONTROL_PRIMARY_PAINT}`,
  /** The canvas toolbar's labelled 28px action: today's ToolbarButton. */
  toolbar:
    "inline-flex h-7 items-center gap-1.5 rounded px-2 text-dense-1 font-medium text-pb-text transition-colors hover:pb-overlay-paint active:pb-overlay-paint-strong focus-ring",
} as const;

export type ChromeControlLook = keyof typeof CHROME_CONTROL_LOOK;

/* A look whose iconOnly box is its own complete class list, not the 32px
   SQUARE. The toolbar's is today's ToolbarIconButton: a 28px square with a
   muted glyph that brightens on hover, and no label type or padding. */
const OWN_SQUARE: Partial<Record<ChromeControlLook, string>> = {
  toolbar:
    "inline-flex h-7 w-7 items-center justify-center rounded text-pb-text-muted transition-colors hover:pb-overlay-paint hover:text-pb-text active:pb-overlay-paint-strong focus-ring",
};

/* A look whose disabled state is not the fade. The toolbar keeps today's
   muted text and not-allowed cursor. bar and quiet take none: today's top-bar
   and TabStrip/CardChrome buttons have no disabled classes, and ChromeIconButton
   renders their exact class lists. */
const OWN_DISABLED: Partial<Record<ChromeControlLook, string>> = {
  toolbar:
    "disabled:cursor-not-allowed disabled:text-pb-text-muted/60 disabled:pointer-events-none",
  bar: "",
  quiet: "",
};

/** The corners a chrome control takes: the same words as ButtonShape. */
export type ChromeControlShape = Extract<Shape, "rounded" | "pill" | "circle">;

/* The 32px square: a fixed width, no padding, the content centred. With
   rounded-full (pill or circle) it is the 32px circle. */
const SQUARE = "w-8 justify-center px-0";

/* A collapsed label is zero wide, but the flex gap before it still counts.
   Closing the gap keeps the 32px box with a 16px icon (8 + 16 + 8). */
const COLLAPSED = "gap-0";

export interface ChromeControlOptions {
  readonly look: ChromeControlLook;
  /** Corner (C7 word). Omitted, the look keeps its own corner. */
  readonly shape?: ChromeControlShape | undefined;
  /** The square box (32px; the toolbar look's own 28px square). `circle`
   *  always takes it. */
  readonly iconOnly?: boolean | undefined;
  /** The label is collapsed (a rail group with hidden labels). */
  readonly collapsed?: boolean | undefined;
  /** Paints the teal selection. */
  readonly pressed?: boolean | undefined;
  /** Paints the current-page fill; the caller sets aria-current. */
  readonly current?: boolean | undefined;
}

/** The class list of one chrome control. */
export function chromeControlClass({
  look,
  shape,
  iconOnly,
  collapsed,
  pressed,
  current,
}: ChromeControlOptions): string {
  const square = iconOnly || shape === "circle";
  const ownSquare = square ? OWN_SQUARE[look] : undefined;
  return cn(
    ownSquare ?? CHROME_CONTROL_LOOK[look],
    OWN_DISABLED[look] ?? CHROME_CONTROL_DISABLED,
    shape && SHAPE_CLASS[shape],
    square && !ownSquare && SQUARE,
    collapsed && COLLAPSED,
    current && (OWN_CURRENT[look] ?? CHROME_CONTROL_CURRENT),
    pressed && CHROME_CONTROL_PRESSED,
  );
}
